import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ArrowLeft, Plus, FileText, ClipboardCheck, AlertTriangle, CheckCircle2, 
  Clock, Calendar, Trash2, Edit, Building2, Phone, XCircle, MapPin, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PERMIT_TYPES = [
  { value: 'building', label: 'Building Permit', inspections: ['foundation', 'rough_framing', 'insulation', 'drywall', 'final'] },
  { value: 'electrical', label: 'Electrical Permit', inspections: ['rough_electrical', 'final_electrical'] },
  { value: 'plumbing', label: 'Plumbing Permit', inspections: ['rough_plumbing', 'final_plumbing'] },
  { value: 'mechanical', label: 'Mechanical/HVAC Permit', inspections: ['rough_mechanical', 'final_mechanical'] },
  { value: 'demolition', label: 'Demolition Permit', inspections: [] },
  { value: 'grading', label: 'Grading Permit', inspections: [] },
  { value: 'roofing', label: 'Roofing Permit', inspections: ['final'] },
  { value: 'other', label: 'Other', inspections: [] }
];

const PERMIT_STATUS = {
  not_applied: { label: 'Not Applied', color: 'bg-slate-100 text-slate-700' },
  applied: { label: 'Applied', color: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  issued: { label: 'Issued', color: 'bg-emerald-100 text-emerald-700' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-700' }
};

const INSPECTION_TYPES = [
  { value: 'foundation', label: 'Foundation' },
  { value: 'rough_framing', label: 'Rough Framing' },
  { value: 'rough_electrical', label: 'Rough Electrical' },
  { value: 'rough_plumbing', label: 'Rough Plumbing' },
  { value: 'rough_mechanical', label: 'Rough Mechanical' },
  { value: 'insulation', label: 'Insulation' },
  { value: 'drywall', label: 'Drywall' },
  { value: 'final_electrical', label: 'Final Electrical' },
  { value: 'final_plumbing', label: 'Final Plumbing' },
  { value: 'final_mechanical', label: 'Final Mechanical' },
  { value: 'final', label: 'Final Building' },
  { value: 'other', label: 'Other' }
];

const INSPECTION_STATUS = {
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-700', icon: Clock },
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700', icon: Calendar },
  passed: { label: 'Passed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
  re_inspection_needed: { label: 'Re-inspection', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700', icon: XCircle }
};

export default function PermitInspectionPage() {
  const darkMode = document.documentElement.classList.contains('dark');
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('permits');
  const [permits, setPermits] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  
  // Permit Dialog
  const [showPermitDialog, setShowPermitDialog] = useState(false);
  const [editingPermit, setEditingPermit] = useState(null);
  const [permitForm, setPermitForm] = useState({
    permit_type: 'building',
    permit_number: '',
    jurisdiction: '',
    description: '',
    application_date: '',
    issued_date: '',
    expiration_date: '',
    status: 'not_applied',
    fee_amount: '',
    jurisdiction_contact: '',
    jurisdiction_phone: '',
    notes: ''
  });
  
  // Inspection Dialog
  const [showInspectionDialog, setShowInspectionDialog] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [inspectionForm, setInspectionForm] = useState({
    permit_id: '',
    inspection_type: 'foundation',
    description: '',
    scheduled_date: '',
    scheduled_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [permitsRes, inspectionsRes, projectRes] = await Promise.all([
        axios.get(`${API}/api/projects/${projectId}/permits`),
        axios.get(`${API}/api/projects/${projectId}/permit-inspections`),
        axios.get(`${API}/api/projects/${projectId}`)
      ]);
      setPermits(permitsRes.data.permits || []);
      setInspections(inspectionsRes.data.inspections || []);
      setProject(projectRes.data);
      
      // Auto-fill jurisdiction from project site address
      const siteAddr = projectRes.data?.site_address;
      if (siteAddr) {
        const jurisdiction = [siteAddr.city, siteAddr.state].filter(Boolean).join(', ');
        if (jurisdiction) {
          setPermitForm(prev => ({ ...prev, jurisdiction }));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get suggested inspections for a permit type
  const getSuggestedInspections = (permitType) => {
    const permit = PERMIT_TYPES.find(p => p.value === permitType);
    return permit?.inspections || [];
  };

  // Create suggested inspections when permit is created
  const createSuggestedInspections = async (permitId, permitType) => {
    const suggested = getSuggestedInspections(permitType);
    for (const inspType of suggested) {
      try {
        await axios.post(`${API}/api/projects/${projectId}/permit-inspections`, {
          permit_id: permitId,
          inspection_type: inspType,
          description: `Required for ${PERMIT_TYPES.find(p => p.value === permitType)?.label}`
        });
      } catch (e) {
        console.error('Failed to create inspection:', e);
      }
    }
    // Refresh inspections list
    const res = await axios.get(`${API}/api/projects/${projectId}/permit-inspections`);
    setInspections(res.data.inspections || []);
  };

  // Permit handlers
  const [autoCreateInspections, setAutoCreateInspections] = useState(true);
  
  const handlePermitSubmit = async () => {
    if (!permitForm.permit_type || !permitForm.jurisdiction) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const data = {
        ...permitForm,
        fee_amount: permitForm.fee_amount ? parseFloat(permitForm.fee_amount) : null
      };
      
      if (editingPermit) {
        const res = await axios.put(`${API}/api/projects/${projectId}/permits/${editingPermit.id}`, data);
        setPermits(permits.map(p => p.id === editingPermit.id ? res.data.permit : p));
        toast.success('Permit updated');
      } else {
        const res = await axios.post(`${API}/api/projects/${projectId}/permits`, data);
        setPermits([...permits, res.data.permit]);
        
        // Auto-create suggested inspections
        if (autoCreateInspections) {
          const suggested = getSuggestedInspections(data.permit_type);
          if (suggested.length > 0) {
            await createSuggestedInspections(res.data.permit.id, data.permit_type);
            toast.success(`Permit added with ${suggested.length} suggested inspections`);
          } else {
            toast.success('Permit added');
          }
        } else {
          toast.success('Permit added');
        }
      }
      setShowPermitDialog(false);
      resetPermitForm();
    } catch (error) {
      toast.error('Failed to save permit');
    }
  };

  const handleDeletePermit = async (permitId) => {
    if (!window.confirm('Delete this permit?')) return;
    try {
      await axios.delete(`${API}/api/projects/${projectId}/permits/${permitId}`);
      setPermits(permits.filter(p => p.id !== permitId));
      toast.success('Permit deleted');
    } catch (error) {
      toast.error('Failed to delete permit');
    }
  };

  const resetPermitForm = () => {
    setPermitForm({
      permit_type: 'building',
      permit_number: '',
      jurisdiction: '',
      description: '',
      application_date: '',
      issued_date: '',
      expiration_date: '',
      status: 'not_applied',
      fee_amount: '',
      jurisdiction_contact: '',
      jurisdiction_phone: '',
      notes: ''
    });
    setEditingPermit(null);
  };

  const openEditPermit = (permit) => {
    setEditingPermit(permit);
    setPermitForm({
      permit_type: permit.permit_type || 'building',
      permit_number: permit.permit_number || '',
      jurisdiction: permit.jurisdiction || '',
      description: permit.description || '',
      application_date: permit.application_date?.split('T')[0] || '',
      issued_date: permit.issued_date?.split('T')[0] || '',
      expiration_date: permit.expiration_date?.split('T')[0] || '',
      status: permit.status || 'not_applied',
      fee_amount: permit.fee_amount?.toString() || '',
      jurisdiction_contact: permit.jurisdiction_contact || '',
      jurisdiction_phone: permit.jurisdiction_phone || '',
      notes: permit.notes || ''
    });
    setShowPermitDialog(true);
  };

  // Inspection handlers
  const handleInspectionSubmit = async () => {
    if (!inspectionForm.inspection_type) {
      toast.error('Please select inspection type');
      return;
    }

    try {
      if (editingInspection) {
        const res = await axios.put(`${API}/api/projects/${projectId}/permit-inspections/${editingInspection.id}`, inspectionForm);
        setInspections(inspections.map(i => i.id === editingInspection.id ? res.data.inspection : i));
        toast.success('Inspection updated');
      } else {
        const res = await axios.post(`${API}/api/projects/${projectId}/permit-inspections`, inspectionForm);
        setInspections([...inspections, res.data.inspection]);
        toast.success('Inspection scheduled');
      }
      setShowInspectionDialog(false);
      resetInspectionForm();
    } catch (error) {
      toast.error('Failed to save inspection');
    }
  };

  const handleDeleteInspection = async (inspectionId) => {
    if (!window.confirm('Delete this inspection?')) return;
    try {
      await axios.delete(`${API}/api/projects/${projectId}/permit-inspections/${inspectionId}`);
      setInspections(inspections.filter(i => i.id !== inspectionId));
      toast.success('Inspection deleted');
    } catch (error) {
      toast.error('Failed to delete inspection');
    }
  };

  const handleUpdateInspectionStatus = async (inspectionId, status, resultNotes = '') => {
    try {
      const updateData = { 
        status,
        ...(status === 'passed' || status === 'failed' ? { completed_date: new Date().toISOString() } : {}),
        ...(resultNotes ? { result_notes: resultNotes } : {})
      };
      const res = await axios.put(`${API}/api/projects/${projectId}/permit-inspections/${inspectionId}`, updateData);
      setInspections(inspections.map(i => i.id === inspectionId ? res.data.inspection : i));
      toast.success('Inspection status updated');
    } catch (error) {
      toast.error('Failed to update inspection');
    }
  };

  const resetInspectionForm = () => {
    setInspectionForm({
      permit_id: '',
      inspection_type: 'foundation',
      description: '',
      scheduled_date: '',
      scheduled_time: '',
      notes: ''
    });
    setEditingInspection(null);
  };

  const openEditInspection = (inspection) => {
    setEditingInspection(inspection);
    setInspectionForm({
      permit_id: inspection.permit_id || '',
      inspection_type: inspection.inspection_type || 'foundation',
      description: inspection.description || '',
      scheduled_date: inspection.scheduled_date?.split('T')[0] || '',
      scheduled_time: inspection.scheduled_time || '',
      notes: inspection.notes || ''
    });
    setShowInspectionDialog(true);
  };

  // Summary stats
  const permitStats = {
    total: permits.length,
    issued: permits.filter(p => p.status === 'issued').length,
    pending: permits.filter(p => ['applied', 'under_review'].includes(p.status)).length
  };
  
  const inspectionStats = {
    total: inspections.length,
    passed: inspections.filter(i => i.status === 'passed').length,
    scheduled: inspections.filter(i => i.status === 'scheduled').length,
    failed: inspections.filter(i => i.status === 'failed' || i.status === 're_inspection_needed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#70170F]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 md:p-6 ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-[#F5F3EF]'}`}>
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1918]">Permits & Inspections</h1>
            <p className="text-muted-foreground">{project?.name || 'Project'}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="permits" className="gap-2">
                <FileText className="w-4 h-4" />
                Permits ({permits.length})
              </TabsTrigger>
              <TabsTrigger value="inspections" className="gap-2">
                <ClipboardCheck className="w-4 h-4" />
                Inspections ({inspections.length})
              </TabsTrigger>
            </TabsList>
            <Button 
              onClick={() => activeTab === 'permits' ? (resetPermitForm(), setShowPermitDialog(true)) : (resetInspectionForm(), setShowInspectionDialog(true))} 
              className="bg-[#70170F] hover:bg-[#8B1E14]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {activeTab === 'permits' ? 'Permit' : 'Inspection'}
            </Button>
          </div>

          {/* Permits Tab */}
          <TabsContent value="permits">
            {/* Permit Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-slate-100">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{permitStats.total}</p>
                      <p className="text-xs text-muted-foreground">Total Permits</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{permitStats.issued}</p>
                      <p className="text-xs text-muted-foreground">Issued</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{permitStats.pending}</p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {permits.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="font-medium mb-2">No Permits Added</h3>
                  <p className="text-sm text-muted-foreground mb-4">Track building permits for this project</p>
                  <Button onClick={() => setShowPermitDialog(true)} className="bg-[#70170F] hover:bg-[#8B1E14]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Permit
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {permits.map(permit => {
                  const statusConfig = PERMIT_STATUS[permit.status] || PERMIT_STATUS.not_applied;
                  return (
                    <Card key={permit.id}>
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-blue-100">
                              <FileText className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">
                                  {PERMIT_TYPES.find(t => t.value === permit.permit_type)?.label || permit.permit_type}
                                </h3>
                                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Building2 className="w-4 h-4" />
                                {permit.jurisdiction}
                                {permit.permit_number && <span>• #{permit.permit_number}</span>}
                              </div>
                              {permit.fee_amount && (
                                <p className="text-sm mt-1">Fee: ${permit.fee_amount.toLocaleString()}</p>
                              )}
                              {permit.expiration_date && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Expires: {new Date(permit.expiration_date).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {permit.jurisdiction_phone && (
                              <a href={`tel:${permit.jurisdiction_phone}`} className="p-2 hover:bg-slate-100 rounded-lg">
                                <Phone className="w-4 h-4 text-muted-foreground" />
                              </a>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openEditPermit(permit)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeletePermit(permit.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Inspections Tab */}
          <TabsContent value="inspections">
            {/* Inspection Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-slate-100">
                      <ClipboardCheck className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{inspectionStats.total}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{inspectionStats.scheduled}</p>
                      <p className="text-xs text-muted-foreground">Scheduled</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{inspectionStats.passed}</p>
                      <p className="text-xs text-muted-foreground">Passed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-100">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{inspectionStats.failed}</p>
                      <p className="text-xs text-muted-foreground">Failed/Re-inspect</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {inspections.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="font-medium mb-2">No Inspections Scheduled</h3>
                  <p className="text-sm text-muted-foreground mb-4">Track building inspections for this project</p>
                  <Button onClick={() => setShowInspectionDialog(true)} className="bg-[#70170F] hover:bg-[#8B1E14]">
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule First Inspection
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {inspections.map(inspection => {
                  const statusConfig = INSPECTION_STATUS[inspection.status] || INSPECTION_STATUS.pending;
                  const StatusIcon = statusConfig.icon;
                  const linkedPermit = permits.find(p => p.id === inspection.permit_id);
                  
                  return (
                    <Card key={inspection.id}>
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
                              <StatusIcon className={`w-6 h-6 ${statusConfig.color.split(' ')[1]}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">
                                  {INSPECTION_TYPES.find(t => t.value === inspection.inspection_type)?.label || inspection.inspection_type}
                                </h3>
                                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                              </div>
                              {linkedPermit && (
                                <p className="text-sm text-muted-foreground">
                                  Permit: {PERMIT_TYPES.find(t => t.value === linkedPermit.permit_type)?.label}
                                </p>
                              )}
                              {inspection.scheduled_date && (
                                <div className="flex items-center gap-1 text-sm mt-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(inspection.scheduled_date).toLocaleDateString()}
                                  {inspection.scheduled_time && ` at ${inspection.scheduled_time}`}
                                </div>
                              )}
                              {inspection.result_notes && (
                                <p className="text-sm text-muted-foreground mt-1">{inspection.result_notes}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {inspection.status === 'scheduled' && (
                              <>
                                <Button size="sm" variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" onClick={() => handleUpdateInspectionStatus(inspection.id, 'passed')}>
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Pass
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-600 hover:bg-red-50" onClick={() => handleUpdateInspectionStatus(inspection.id, 'failed')}>
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Fail
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openEditInspection(inspection)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteInspection(inspection.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Permit Dialog */}
      <Dialog open={showPermitDialog} onOpenChange={(open) => { if (!open) resetPermitForm(); setShowPermitDialog(open); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPermit ? 'Edit Permit' : 'Add Permit'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Site Address Info - Auto-fill source */}
            {project?.site_address && !editingPermit && (
              <div className="p-3 rounded-lg bg-teal-50 border border-teal-200">
                <div className="flex items-center gap-2 text-sm text-teal-700">
                  <MapPin className="w-4 h-4" />
                  <span className="font-medium">Site Address:</span>
                  <span>
                    {[project.site_address.street, project.site_address.city, project.site_address.state].filter(Boolean).join(', ')}
                  </span>
                </div>
                <p className="text-xs text-teal-600 mt-1 ml-6">Jurisdiction auto-filled from site address</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Permit Type *</Label>
                <Select value={permitForm.permit_type} onValueChange={(v) => setPermitForm({...permitForm, permit_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERMIT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          {type.label}
                          {type.inspections.length > 0 && (
                            <Badge variant="secondary" className="text-xs">{type.inspections.length} inspections</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Permit Number</Label>
                <Input value={permitForm.permit_number} onChange={(e) => setPermitForm({...permitForm, permit_number: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Jurisdiction (City/County) *</Label>
              <Input value={permitForm.jurisdiction} onChange={(e) => setPermitForm({...permitForm, jurisdiction: e.target.value})} placeholder="e.g., City of Denver" />
            </div>
            
            {/* Auto-create inspections checkbox */}
            {!editingPermit && getSuggestedInspections(permitForm.permit_type).length > 0 && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="auto-inspections" 
                    checked={autoCreateInspections}
                    onCheckedChange={setAutoCreateInspections}
                  />
                  <label htmlFor="auto-inspections" className="text-sm font-medium text-blue-700 cursor-pointer flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Auto-create required inspections
                  </label>
                </div>
                <p className="text-xs text-blue-600 mt-1 ml-6">
                  Will create: {getSuggestedInspections(permitForm.permit_type).map(i => 
                    INSPECTION_TYPES.find(t => t.value === i)?.label
                  ).join(', ')}
                </p>
              </div>
            )}
            
            <div>
              <Label>Status</Label>
              <Select value={permitForm.status} onValueChange={(v) => setPermitForm({...permitForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PERMIT_STATUS).map(([value, config]) => (
                    <SelectItem key={value} value={value}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Applied Date</Label>
                <Input type="date" value={permitForm.application_date} onChange={(e) => setPermitForm({...permitForm, application_date: e.target.value})} />
              </div>
              <div>
                <Label>Issued Date</Label>
                <Input type="date" value={permitForm.issued_date} onChange={(e) => setPermitForm({...permitForm, issued_date: e.target.value})} />
              </div>
              <div>
                <Label>Expiration Date</Label>
                <Input type="date" value={permitForm.expiration_date} onChange={(e) => setPermitForm({...permitForm, expiration_date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Fee Amount</Label>
              <Input type="number" step="0.01" value={permitForm.fee_amount} onChange={(e) => setPermitForm({...permitForm, fee_amount: e.target.value})} placeholder="0.00" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input value={permitForm.jurisdiction_contact} onChange={(e) => setPermitForm({...permitForm, jurisdiction_contact: e.target.value})} />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input value={permitForm.jurisdiction_phone} onChange={(e) => setPermitForm({...permitForm, jurisdiction_phone: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={permitForm.notes} onChange={(e) => setPermitForm({...permitForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetPermitForm(); setShowPermitDialog(false); }}>Cancel</Button>
            <Button onClick={handlePermitSubmit} className="bg-[#70170F] hover:bg-[#8B1E14]">
              {editingPermit ? 'Update' : 'Add'} Permit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inspection Dialog */}
      <Dialog open={showInspectionDialog} onOpenChange={(open) => { if (!open) resetInspectionForm(); setShowInspectionDialog(open); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingInspection ? 'Edit Inspection' : 'Schedule Inspection'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Inspection Type *</Label>
              <Select value={inspectionForm.inspection_type} onValueChange={(v) => setInspectionForm({...inspectionForm, inspection_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INSPECTION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {permits.length > 0 && (
              <div>
                <Label>Linked Permit (Optional)</Label>
                <Select value={inspectionForm.permit_id || 'none'} onValueChange={(v) => setInspectionForm({...inspectionForm, permit_id: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Select permit..." /></SelectTrigger>
                  <SelectContent>
                    {/* Radix Select v2 throws on value="" — use a 'none' sentinel */}
                    <SelectItem value="none">None</SelectItem>
                    {permits.map(permit => (
                      <SelectItem key={permit.id} value={permit.id}>
                        {PERMIT_TYPES.find(t => t.value === permit.permit_type)?.label} - {permit.jurisdiction}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scheduled Date</Label>
                <Input type="date" value={inspectionForm.scheduled_date} onChange={(e) => setInspectionForm({...inspectionForm, scheduled_date: e.target.value})} />
              </div>
              <div>
                <Label>Scheduled Time</Label>
                <Input type="time" value={inspectionForm.scheduled_time} onChange={(e) => setInspectionForm({...inspectionForm, scheduled_time: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={inspectionForm.description} onChange={(e) => setInspectionForm({...inspectionForm, description: e.target.value})} rows={2} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={inspectionForm.notes} onChange={(e) => setInspectionForm({...inspectionForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetInspectionForm(); setShowInspectionDialog(false); }}>Cancel</Button>
            <Button onClick={handleInspectionSubmit} className="bg-[#70170F] hover:bg-[#8B1E14]">
              {editingInspection ? 'Update' : 'Schedule'} Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
